export function parseDecimal(value: string | null): number {
  if (value === null || value === '') return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function parseDecimalOrNull(value: string | null): number | null {
  if (value === null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

// ── Exact decimal-string arithmetic ──────────────────────────────────────
// tech-spec-allocation-workbench.md §4: the Rivonia allocation residual is
// zero-tolerance — a residual that is truly zero must never read as a
// float-drift artefact (`50 - 25 - 25` can land on `-1.7e-15` in IEEE-754),
// and a residual that is truly non-zero must never collapse to zero
// (`50 - 49.999` is `0.0009999999999994458` as a float, not `0.001`).
// Everything below scales every operand to shared integer precision and
// does the arithmetic in BigInt, so both the zero check and the displayed
// value are exact.

function decimalPlaces(value: string): number {
  const dot = value.indexOf('.');
  return dot === -1 ? 0 : value.length - dot - 1;
}

function toScaledBigInt(value: string, scale: number): bigint {
  const trimmed = value.trim();
  const negative = trimmed.startsWith('-');
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const [intPart = '', fracPart = ''] = unsigned.split('.');
  const paddedFrac = fracPart.padEnd(scale, '0').slice(0, scale);
  const safeIntPart = intPart.length > 0 ? intPart : '0';
  const magnitude = BigInt(`${safeIntPart}${paddedFrac}`);
  return negative ? -magnitude : magnitude;
}

// Thousands-separator grouping without a backtracking-prone regex: chunk the
// digit string into groups of three from the right, then rejoin with commas.
function addThousandsSeparators(digits: string): string {
  const chunks: string[] = [];
  for (let end = digits.length; end > 0; end -= 3) {
    chunks.unshift(digits.slice(Math.max(0, end - 3), end));
  }
  return chunks.join(',');
}

function formatScaledBigInt(scaled: bigint, scale: number): string {
  const negative = scaled < 0n;
  const magnitude = negative ? -scaled : scaled;
  const digits = magnitude.toString().padStart(scale + 1, '0');
  const sign = negative && magnitude !== 0n ? '-' : '';
  if (scale === 0) return `${sign}${digits}`;
  const splitAt = digits.length - scale;
  return `${sign}${digits.slice(0, splitAt)}.${digits.slice(splitAt)}`;
}

/** Exact `Σ values`, scaled to the widest decimal precision present. */
export function sumDecimalStrings(values: readonly string[]): string {
  if (values.length === 0) return '0';
  const scale = Math.max(...values.map(decimalPlaces));
  const total = values.reduce((acc, value) => acc + toScaledBigInt(value, scale), 0n);
  return formatScaledBigInt(total, scale);
}

/** Exact `minuend − Σ subtrahends`. */
export function subtractDecimalStrings(minuend: string, subtrahends: readonly string[]): string {
  const scale = Math.max(decimalPlaces(minuend), 0, ...subtrahends.map(decimalPlaces));
  const total = subtrahends.reduce(
    (acc, value) => acc - toScaledBigInt(value, scale),
    toScaledBigInt(minuend, scale),
  );
  return formatScaledBigInt(total, scale);
}

/** Exact `|value|`: the magnitude with any sign dropped (`"-3.50"` → `"3.50"`, `"-0"` → `"0"`). */
export function absDecimalString(value: string): string {
  const scale = decimalPlaces(value);
  const scaled = toScaledBigInt(value, scale);
  return formatScaledBigInt(scaled < 0n ? -scaled : scaled, scale);
}

/** True only when a decimal string is exactly zero — an exact BigInt check, never an epsilon. */
export function isZeroDecimalString(value: string): boolean {
  return toScaledBigInt(value, decimalPlaces(value)) === 0n;
}

/**
 * Presentation only: adds thousands separators to a decimal string's integer
 * part without touching its fractional digits, so a value produced by the
 * exact helpers above (e.g. `"0.001"`) is never re-rounded on the way to the
 * screen the way a fixed-2dp formatter (`formatMt`-style) would.
 */
export function formatDecimalDisplay(value: string): string {
  const trimmed = value.trim();
  const negative = trimmed.startsWith('-');
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const [intPart = '', fracPart] = unsigned.split('.');
  const safeIntPart = intPart.length > 0 ? intPart : '0';
  const withSeparators = addThousandsSeparators(safeIntPart);
  const body = fracPart ? `${withSeparators}.${fracPart}` : withSeparators;
  return negative && body !== '0' ? `-${body}` : body;
}
